/**
 * ResizableImageExtension
 *
 * Extends TipTap's Image extension with:
 * - Corner drag handles for resizing (aspect-ratio locked)
 * - Alignment support (left, center, right)
 * - GCS file name attribute for signed URL refresh
 * - Max height enforcement for pagination safety
 */

import Image from "@tiptap/extension-image";

// Max image height to prevent PaginationPlus infinite page loop
// Page content height = 1056 - 72*2 - 20*2 = 872px, leave margin
const MAX_IMAGE_HEIGHT = 800;
const MIN_IMAGE_WIDTH = 50;

export type ImageAlignment = "left" | "center" | "right";

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      gcsFileName: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-gcs-file-name"),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.gcsFileName) return {};
          return { "data-gcs-file-name": attributes.gcsFileName };
        },
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const w = element.getAttribute("width") || element.style.width;
          return w ? parseInt(w, 10) || null : null;
        },
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const h = element.getAttribute("height") || element.style.height;
          return h ? parseInt(h, 10) || null : null;
        },
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      alignment: {
        default: "center",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-alignment") || "center",
        renderHTML: (attributes: Record<string, any>) => {
          return { "data-alignment": attributes.alignment || "center" };
        },
      },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      // ============================================================
      // DOM STRUCTURE
      // ============================================================
      // <div class="resizable-image-wrapper" data-alignment="center">
      //   <div class="resizable-image-container">
      //     <img ...>
      //     <div class="resize-handle resize-handle-br">
      //     <div class="resize-handle resize-handle-bl">
      //   </div>
      //   <div class="image-alignment-toolbar">  (shown when selected)
      //     <button>⬅</button> <button>⬛</button> <button>➡</button>
      //   </div>
      // </div>

      const wrapper = document.createElement("div");
      wrapper.classList.add("resizable-image-wrapper");
      wrapper.setAttribute(
        "data-alignment",
        node.attrs.alignment || "center"
      );
      wrapper.draggable = false;

      const container = document.createElement("div");
      container.classList.add("resizable-image-container");
      container.draggable = false;

      const img = document.createElement("img");
      const imgSrc = node.attrs.src || "";
      // Backend image-proxy responses are cross-origin (work.leegal.ai vs
      // api.leegal.ai) and served behind helmet's Cross-Origin-Resource-Policy:
      // same-origin, which blocks a plain <img> (ERR_BLOCKED_BY_RESPONSE). The
      // credentialed CORS load is eligible. Set it ONLY for
      // backend URLs — setting it on an arbitrary external image that lacks CORS
      // headers would itself break that image.
      if (/\/api\/image-proxy\//.test(imgSrc)) {
        img.crossOrigin = "use-credentials";
      }
      img.src = imgSrc;
      if (node.attrs.alt) img.alt = node.attrs.alt;
      if (node.attrs.title) img.title = node.attrs.title;
      if (node.attrs.gcsFileName) {
        img.setAttribute("data-gcs-file-name", node.attrs.gcsFileName);
      }
      img.classList.add("tiptap-image");
      img.setAttribute("loading", "lazy");
      img.draggable = false;

      // Helper: apply dimensions and toggle container sizing mode
      function applyDimensions(width: number | null, height: number | null) {
        if (width) {
          // Explicit dimensions → container wraps tightly around the image
          container.style.display = "inline-block";
          container.style.width = "";
          img.style.width = `${width}px`;
          img.style.height = height ? `${height}px` : "auto";
        } else {
          // No dimensions → container sizes to fit the image at its natural dimensions
          // Using inline-block so container hugs the image (avoids circular dependency
          // where width:100% of a flex parent with no width = 0px)
          container.style.display = "inline-block";
          container.style.width = "";
          img.style.width = "auto";
          img.style.height = "auto";
        }
      }

      applyDimensions(node.attrs.width, node.attrs.height);

      // Keep a mutable reference to the current node attrs so that
      // handlers (alignment, resize) always use the latest values.
      let currentAttrs = { ...node.attrs };

      container.appendChild(img);

      // ============================================================
      // RESIZE HANDLES
      // ============================================================
      const handleBR = document.createElement("div");
      handleBR.classList.add("resize-handle", "resize-handle-br");
      handleBR.title = "Drag to resize";
      container.appendChild(handleBR);

      const handleBL = document.createElement("div");
      handleBL.classList.add("resize-handle", "resize-handle-bl");
      handleBL.title = "Drag to resize";
      container.appendChild(handleBL);

      // Track whether we're selected (used by updateSelected closure)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let isSelected = false;

      // ============================================================
      // ALIGNMENT TOOLBAR
      // ============================================================
      const toolbar = document.createElement("div");
      toolbar.classList.add("image-alignment-toolbar");
      toolbar.contentEditable = "false";

      const alignments: { value: ImageAlignment; icon: string; label: string }[] = [
        { value: "left", icon: "⬅", label: "Align left" },
        { value: "center", icon: "⬛", label: "Align center" },
        { value: "right", icon: "➡", label: "Align right" },
      ];

      alignments.forEach(({ value, icon, label }) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.classList.add("image-alignment-btn");
        btn.setAttribute("data-alignment", value);
        btn.title = label;
        btn.textContent = icon;
        if (node.attrs.alignment === value) {
          btn.classList.add("active");
        }
        btn.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const pos = typeof getPos === "function" ? getPos() : undefined;
          if (pos !== undefined) {
            editor.chain().focus()
              .command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...currentAttrs,
                  alignment: value,
                });
                return true;
              })
              .run();
          }
        });
        toolbar.appendChild(btn);
      });

      wrapper.appendChild(container);
      wrapper.appendChild(toolbar);

      // ============================================================
      // RESIZE LOGIC
      // ============================================================
      function startResize(
        e: MouseEvent,
        corner: "br" | "bl"
      ) {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startWidth = img.offsetWidth;
        const startHeight = img.offsetHeight;
        const aspectRatio = startWidth / startHeight;

        // Add resizing class for visual feedback
        wrapper.classList.add("resizing");

        const onMouseMove = (ev: MouseEvent) => {
          const dx = corner === "br"
            ? ev.clientX - startX
            : startX - ev.clientX;

          let newWidth = Math.max(MIN_IMAGE_WIDTH, startWidth + dx);
          let newHeight = Math.round(newWidth / aspectRatio);

          // Enforce max height for pagination safety
          if (newHeight > MAX_IMAGE_HEIGHT) {
            newHeight = MAX_IMAGE_HEIGHT;
            newWidth = Math.round(newHeight * aspectRatio);
          }

          img.style.width = `${newWidth}px`;
          img.style.height = `${newHeight}px`;
        };

        const onMouseUp = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          wrapper.classList.remove("resizing");

          // Commit new dimensions to the node
          const finalWidth = img.offsetWidth;
          const finalHeight = img.offsetHeight;
          currentAttrs = { ...currentAttrs, width: finalWidth, height: finalHeight };
          const pos = typeof getPos === "function" ? getPos() : undefined;
          if (pos !== undefined) {
            editor.chain().focus()
              .command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...currentAttrs,
                });
                return true;
              })
              .run();
          }
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      }

      handleBR.addEventListener("mousedown", (e) => startResize(e, "br"));
      handleBL.addEventListener("mousedown", (e) => startResize(e, "bl"));

      // ============================================================
      // SELECTION HANDLING
      // ============================================================
      function updateSelected(selected: boolean) {
        isSelected = selected;
        wrapper.classList.toggle("selected", selected);
      }

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== "image") return false;

          // Keep mutable attrs reference in sync
          currentAttrs = { ...updatedNode.attrs };

          // Update src if changed (e.g. base64 preview swapped for the uploaded
          // image-proxy URL). Re-apply the credentialed CORS mode for backend
          // URLs so the swapped image isn't CORP-blocked.
          if (updatedNode.attrs.src !== img.src) {
            const newSrc = updatedNode.attrs.src || "";
            if (/\/api\/image-proxy\//.test(newSrc)) {
              img.crossOrigin = "use-credentials";
            } else {
              img.removeAttribute("crossorigin");
            }
            img.src = newSrc;
          }

          // Update dimensions + container sizing mode
          applyDimensions(updatedNode.attrs.width, updatedNode.attrs.height);

          // Update alignment
          wrapper.setAttribute(
            "data-alignment",
            updatedNode.attrs.alignment || "center"
          );

          // Update toolbar active state
          toolbar.querySelectorAll(".image-alignment-btn").forEach((btn) => {
            const btnAlign = btn.getAttribute("data-alignment");
            btn.classList.toggle(
              "active",
              btnAlign === (updatedNode.attrs.alignment || "center")
            );
          });

          // Update GCS file name
          if (updatedNode.attrs.gcsFileName) {
            img.setAttribute(
              "data-gcs-file-name",
              updatedNode.attrs.gcsFileName
            );
          }

          return true;
        },
        selectNode: () => updateSelected(true),
        deselectNode: () => updateSelected(false),
        destroy: () => {
          // Cleanup if needed
        },
      };
    };
  },
});

export default ResizableImage;
